import { Stack, Typography } from "@mui/material";
import { type FC, type SyntheticEvent, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemAutoComplete, type SystemAutoCompleteOption } from "../../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { SystemValidationTextField } from "../../../components/UIToolkit/Inputs/SystemValidationTextField";
import SystemDialog from "../../../components/UIToolkit/Popups/Dialog";
import { usePbjAuditEmployees, usePbjExcludeEmployee } from "../hooks/usePbjAudit";

type Props = {
	open: boolean;
	onClose: () => void;
	departmentId: string;
	startDate: string;
	endDate: string;
	/** RAW synced pbj ids already excluded — filtered out of the picker so you can't double-exclude. */
	excludedPbjIds: Set<string>;
};

/**
 * PBJ Audit → "Excluded from PBJ" → Exclude an employee: pick a currently-active employee from the
 * facility-group roster (`usePbjAuditEmployees` for the viewed range) and block them from the CMS filing
 * with a mandatory, non-blank reason. Already-excluded employees are dropped from the picker (the parent
 * passes their pbj ids) so an employee can't be excluded twice. Confirm re-uses `usePbjExcludeEmployee`
 * (exclude=true) — its query invalidation refreshes the grid on success. One-step, so it composes
 * `SystemDialog` like the sibling edit-reason dialog rather than the two-step raw-MUI import dialog.
 */
export const ExcludeEmployeeDialog: FC<Props> = ({ open, onClose, departmentId, startDate, endDate, excludedPbjIds }) => {
	const { t } = useTranslation();
	const { data: roster, isLoading: rosterLoading } = usePbjAuditEmployees(startDate, endDate);
	const excludeMutation = usePbjExcludeEmployee();

	const [selected, setSelected] = useState<SystemAutoCompleteOption<string> | null>(null);
	const [reasonDraft, setReasonDraft] = useState("");
	const [reasonSubmitted, setReasonSubmitted] = useState(false);

	// Options = roster minus the already-excluded ids (keyed by RAW pbjId, the exclusion key). Label = name.
	const options = useMemo<SystemAutoCompleteOption<string>[]>(
		// Only the building being written to — in the network view the roster holds every building.
		() =>
			(roster ?? [])
				.filter((employee) => employee.buildingId === departmentId && !excludedPbjIds.has(employee.pbjId))
				.map((employee) => ({ label: employee.name, value: employee.pbjId })),
		[roster, excludedPbjIds, departmentId],
	);

	const trimmedReason = reasonDraft.trim();
	const reasonValid = trimmedReason.length > 0;
	const confirmDisabled = selected === null || !reasonValid;

	const handleClose = useCallback(() => {
		if (excludeMutation.isPending) return;
		setSelected(null);
		setReasonDraft("");
		setReasonSubmitted(false);
		onClose();
	}, [excludeMutation.isPending, onClose]);

	// Exclude the selected employee with the (non-blank) reason. The button is disabled until both an
	// employee is picked and the reason is non-blank; this re-asserts that as the final guard.
	const handleConfirm = useCallback(() => {
		setReasonSubmitted(true);
		if (!selected || !reasonValid || excludeMutation.isPending) return;
		excludeMutation.mutate({ departmentId, pbjEmployeeId: selected.value, exclude: true, reason: trimmedReason }, { onSuccess: () => handleClose() });
	}, [selected, reasonValid, excludeMutation, departmentId, trimmedReason, handleClose]);

	return (
		<SystemDialog
			open={open}
			onClose={handleClose}
			title={t("pbj_model.audit.excluded.exclude_dialog.title")}
			body={
				<Stack spacing={2} sx={{ pt: 1, minWidth: { xs: "auto", sm: 380 } }}>
					<Typography variant="body2" color="text.secondary">
						{t("pbj_model.audit.excluded.exclude_dialog.description")}
					</Typography>
					<SystemAutoComplete<string>
						options={options}
						value={selected}
						onChange={(_: SyntheticEvent, value: SystemAutoCompleteOption<string> | null) => setSelected(value)}
						disabled={rosterLoading}
						fullWidth
						textFieldProps={{
							label: t("pbj_model.audit.excluded.exclude_dialog.employee_label"),
							placeholder: rosterLoading
								? t("pbj_model.audit.excluded.exclude_dialog.employee_loading")
								: t("pbj_model.audit.excluded.exclude_dialog.employee_placeholder"),
							required: true,
						}}
						dataTestId="pbj-exclude-employee-picker"
					/>
					<SystemValidationTextField
						value={reasonDraft}
						onChange={(e) => setReasonDraft(e.target.value)}
						label={t("pbj_model.audit.excluded.exclude_dialog.reason_label")}
						placeholder={t("pbj_model.audit.excluded.exclude_dialog.reason_placeholder")}
						required
						fullWidth
						submitTriggerred={reasonSubmitted}
						validation={{
							performValidation: "onlyAfterInitialChange",
							showValidWhenValid: "never",
							validationCallback: (v) => v.trim().length > 0,
							helperText: { invalid: t("pbj_model.audit.employees.exclusion.reason_required") },
						}}
						dataTestId="pbj-exclude-employee-reason-input"
					/>
				</Stack>
			}
			confirm={t("pbj_model.audit.excluded.exclude_dialog.confirm")}
			confirmButtonVariant="primary"
			confirmDisabled={confirmDisabled}
			confirmLoading={excludeMutation.isPending}
			onConfirm={handleConfirm}
			cancel={t("cancel")}
			dataTestId="pbj-exclude-employee-dialog"
		/>
	);
};
